
import { useState, useEffect } from 'react';
import { GuidedPromptEditorProps } from './prompt-editor/types';
import { PromptHeader } from './prompt-editor/PromptHeader';
import { PromptDisplay } from './prompt-editor/PromptDisplay';
import { EditDialog } from './prompt-editor/EditDialog';
import { PromptSection } from './prompt-editor/PromptSection';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { CircleSlash } from 'lucide-react';

export const GuidedPromptEditor = ({ 
  value, 
  onChange, 
  agentType, 
  isReadOnly = false 
}: GuidedPromptEditorProps) => {
  const [showEditor, setShowEditor] = useState(false);
  const [tempPrompt, setTempPrompt] = useState(value || '');
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  
  // Update temp prompt when value changes (important for initialization)
  useEffect(() => {
    setTempPrompt(value || '');
  }, [value]);

  const promptSections = [
    { 
      title: "Role Definition", 
      description: "Define the agent's primary role and responsibilities",
      examples: [
        "You are an expert recruiter specialized in technical hiring",
        "You are a scheduling assistant that helps manage calendars efficiently"
      ]
    },
    { 
      title: "Tone and Communication Style", 
      description: "Specify how the agent should communicate (formal, friendly, etc.)",
      examples: [
        "Communicate in a professional but friendly tone",
        "Use concise, technical language appropriate for engineers"
      ]
    },
    { 
      title: "Key Capabilities", 
      description: "List the main tasks and actions the agent can perform",
      examples: [
        "Screen candidates based on experience and skills",
        "Coordinate meetings between multiple participants"
      ]
    },
    { 
      title: "Domain Knowledge", 
      description: "Define the specific knowledge areas the agent specializes in",
      examples: [
        "Software engineering hiring processes and best practices",
        "Project management methodologies and terminology"
      ]
    }
  ];

  const handleSave = () => {
    onChange(tempPrompt);
    setShowEditor(false);
    toast.success("Prompt updated successfully");
  };
  
  const handleSectionSelect = (title: string) => {
    setSelectedSection(title);
    setShowEditor(true);
  };
  
  const handleGeneratePrompt = () => {
    // This would typically call an AI service to suggest prompt improvements
    toast.info("AI-suggested prompt improvements feature coming soon!");
  };
  
  const handleReset = () => {
    if (window.confirm("Are you sure you want to reset the prompt to default?")) {
      // This would reset to a default template
      const defaultPrompt = `### Role Definition
You are an AI assistant specializing in ${agentType.toLowerCase()} tasks.

### Tone and Communication Style
Communicate in a professional, helpful manner. Be concise but thorough.

### Key Capabilities
- Understand and respond to user requests related to ${agentType.toLowerCase()}
- Provide accurate information based on available knowledge
- Guide users through complex processes step by step

### Domain Knowledge
Knowledge of ${agentType.toLowerCase()} processes, best practices, and terminology.`;
      
      setTempPrompt(defaultPrompt);
      onChange(defaultPrompt);
      toast.success("Prompt reset to default template");
    }
  };

  return (
    <div className="space-y-4">
      <PromptHeader
        isReadOnly={isReadOnly}
        onEdit={() => setShowEditor(true)}
        onGeneratePrompt={handleGeneratePrompt}
        hasGenerateFeature={!isReadOnly}
      />

      <PromptDisplay
        value={value}
        isReadOnly={isReadOnly}
      />
      
      {!isReadOnly && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          {promptSections.map((section) => (
            <PromptSection
              key={section.title}
              title={section.title}
              description={section.description}
              examples={section.examples}
              onSelect={handleSectionSelect}
            />
          ))}
        </div>
      )}
      
      {!isReadOnly && value && (
        <div className="flex justify-end mt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="text-muted-foreground hover:text-destructive flex items-center gap-1"
          >
            <CircleSlash className="h-4 w-4" />
            Reset to Default
          </Button>
        </div>
      )}

      <EditDialog
        open={showEditor}
        onOpenChange={setShowEditor}
        tempPrompt={tempPrompt}
        onPromptChange={setTempPrompt}
        onSave={handleSave}
        agentType={agentType}
        promptSections={promptSections}
      />
    </div>
  );
};
